import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Home, ShoppingBag, Package, Mail, FileText, Navigation,
  Shield, Truck, ScrollText, HelpCircle, CheckCircle, Clock,
  AlertCircle, Eye, Pencil, Palette, Type,
  ArrowRight, ChevronDown, ChevronUp, Sparkles, ExternalLink,
  LayoutGrid, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import AuthContext from '../../context/AuthContext';
import Base from '../Base';

// ─── Mini Preview Layouts (CSS art representing each page type) ─────────────
const MiniPreview = ({ type }) => {
  const base = 'w-full h-24 rounded-lg overflow-hidden bg-gray-50 border border-gray-100 p-2 flex flex-col gap-1';
  const bar = 'rounded-sm bg-gray-200';
  const accent = 'rounded-sm bg-primary/20';

  const layouts = {
    main: (
      <div className={base}>
        <div className={`${accent} h-10 w-full`} />
        <div className="flex gap-1 flex-1">
          <div className={`${bar} flex-1`} />
          <div className={`${bar} flex-1`} />
          <div className={`${bar} flex-1`} />
        </div>
        <div className={`${bar} h-2 w-3/4`} />
      </div>
    ),
    shop: (
      <div className={base}>
        <div className={`${bar} h-2 w-1/2`} />
        <div className="grid grid-cols-3 gap-1 flex-1">
          {[...Array(6)].map((_, i) => <div key={i} className={`${i < 3 ? accent : bar} rounded-sm`} />)}
        </div>
      </div>
    ),
    product: (
      <div className={base}>
        <div className="flex gap-2 flex-1">
          <div className={`${accent} w-1/2 rounded-sm`} />
          <div className="flex-1 flex flex-col gap-1">
            <div className={`${bar} h-2 w-full`} />
            <div className={`${bar} h-2 w-3/4`} />
            <div className={`${bar} h-2 w-1/2`} />
            <div className={`${accent} h-3 w-16 mt-auto`} />
          </div>
        </div>
      </div>
    ),
    contact: (
      <div className={base}>
        <div className={`${bar} h-2 w-1/3 mx-auto`} />
        <div className="flex gap-2 flex-1">
          <div className="flex-1 flex flex-col gap-1">
            <div className={`${bar} h-2 w-full`} />
            <div className={`${bar} h-2 w-full`} />
            <div className={`${bar} h-4 w-full`} />
          </div>
          <div className={`${accent} w-1/3 rounded-sm`} />
        </div>
      </div>
    ),
    about: (
      <div className={base}>
        <div className={`${accent} h-8 w-full`} />
        <div className="flex gap-1 flex-1">
          <div className={`${bar} flex-1`} />
          <div className="flex-1 flex flex-col gap-0.5">
            <div className={`${bar} h-1.5 w-full`} />
            <div className={`${bar} h-1.5 w-3/4`} />
            <div className={`${bar} h-1.5 w-1/2`} />
          </div>
        </div>
      </div>
    ),
    navbar: (
      <div className={base + ' justify-center'}>
        <div className="flex items-center gap-2 px-1">
          <div className={`${accent} h-3 w-8 rounded-sm`} />
          <div className="flex gap-1 ml-auto">
            <div className={`${bar} h-1.5 w-6`} />
            <div className={`${bar} h-1.5 w-6`} />
            <div className={`${bar} h-1.5 w-6`} />
          </div>
          <div className={`${bar} h-3 w-3 rounded-full ml-1`} />
        </div>
      </div>
    ),
    footer: (
      <div className={base + ' justify-end'}>
        <div className="flex gap-2">
          <div className="flex-1 flex flex-col gap-0.5">
            <div className={`${accent} h-2 w-10`} />
            <div className={`${bar} h-1 w-full`} />
            <div className={`${bar} h-1 w-3/4`} />
          </div>
          <div className="flex-1 flex flex-col gap-0.5">
            <div className={`${bar} h-1 w-8`} />
            <div className={`${bar} h-1 w-10`} />
            <div className={`${bar} h-1 w-6`} />
          </div>
          <div className="flex-1 flex flex-col gap-0.5">
            <div className={`${bar} h-1 w-6`} />
            <div className={`${bar} h-1 w-8`} />
          </div>
        </div>
        <div className={`${bar} h-1 w-1/2 mx-auto mt-1`} />
      </div>
    ),
    legal: (
      <div className={base}>
        <div className={`${bar} h-2 w-1/2`} />
        <div className={`${bar} h-1 w-full`} />
        <div className={`${bar} h-1 w-full`} />
        <div className={`${bar} h-1 w-3/4`} />
        <div className={`${bar} h-1 w-full`} />
        <div className={`${bar} h-1 w-2/3`} />
      </div>
    ),
  };

  return layouts[type] || layouts.legal;
};

// ─── Page Definitions ───────────────────────────────────────────────────────
const PAGE_DEFS = [
  { id: 'main', title: 'Home Page', description: 'Hero banner, featured products, brand story', icon: Home, category: 'core', preview: 'main' },
  { id: 'shop', title: 'Shop Page', description: 'Product catalog with filters and sorting', icon: ShoppingBag, category: 'core', preview: 'shop' },
  { id: 'product', title: 'Product Page', description: 'Product details, images, and sizing', icon: Package, category: 'core', preview: 'product' },
  { id: 'navbar', title: 'Navigation Bar', description: 'Logo, menu links, and cart icon', icon: Navigation, category: 'layout', preview: 'navbar' },
  { id: 'footer', title: 'Footer', description: 'Contact info, links, and social media', icon: LayoutGrid, category: 'layout', preview: 'footer' },
  { id: 'about', title: 'About Page', description: 'Your brand story and mission', icon: FileText, category: 'content', preview: 'about' },
  { id: 'contact', title: 'Contact Page', description: 'Contact form and business details', icon: Mail, category: 'content', preview: 'contact' },
  { id: 'privacy', title: 'Privacy Policy', description: 'Data handling & privacy terms', icon: Shield, category: 'legal', preview: 'legal' },
  { id: 'shipping', title: 'Shipping & Returns', description: 'Delivery and return policies', icon: Truck, category: 'legal', preview: 'legal' },
  { id: 'terms', title: 'Terms of Service', description: 'Terms and conditions', icon: ScrollText, category: 'legal', preview: 'legal' },
  { id: 'faqs', title: 'FAQs', description: 'Frequently asked questions', icon: HelpCircle, category: 'legal', preview: 'legal' },
];

// Guided setup priority order
const SETUP_ORDER = ['main', 'navbar', 'shop', 'footer', 'product', 'about', 'contact', 'privacy', 'shipping', 'terms', 'faqs'];

const SETUP_MESSAGES = {
  main: { title: 'Start with your Home Page', desc: 'Set your brand identity — hero image, colors, and featured products.' },
  navbar: { title: 'Set up your Navigation', desc: 'Add your logo, menu links, and configure the top bar.' },
  shop: { title: 'Design your Shop Page', desc: 'Configure how your product catalog looks to customers.' },
  footer: { title: 'Complete your Footer', desc: 'Add contact info, social links, and important links.' },
  product: { title: 'Customize Product Page', desc: 'Control how individual product details appear.' },
  about: { title: 'Tell your Brand Story', desc: 'Share your mission, values, and the story behind your brand.' },
  contact: { title: 'Add Contact Details', desc: 'Let customers reach you with a contact form and info.' },
  privacy: { title: 'Add Privacy Policy', desc: 'Required for a professional store — customize the default.' },
  shipping: { title: 'Set Shipping Info', desc: 'Delivery timelines, charges, and return policies.' },
  terms: { title: 'Add Terms of Service', desc: 'Standard terms — edit to match your policies.' },
  faqs: { title: 'Add FAQs', desc: 'Help customers find quick answers.' },
};

// ─── Component ──────────────────────────────────────────────────────────────
const PageDashboard = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [pageStatuses, setPageStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [themeColors, setThemeColors] = useState(null);
  const [showLegal, setShowLegal] = useState(false);

  // ── API calls ─────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      if (!token) { navigate('/login'); return; }
      const headers = { Authorization: `Bearer ${token}` };

      const [dashRes, selRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/template/${templateId}/dashboard/`, { headers }),
        axios.get(`${API_BASE_URL}/api/template/selection`, { headers }),
      ]);

      setPageStatuses(dashRes.data.pages || {});
      if (selRes.data.success && selRes.data.has_selection) {
        setTemplateInfo(selRes.data.template);
      }

      // Try to extract theme colors from main page JSON
      try {
        const mainRes = await axios.get(`${API_BASE_URL}/api/template/${templateId}/page/main`, { headers });
        const mainData = mainRes.data;
        const json = typeof mainData === 'string' ? JSON.parse(mainData) : mainData;
        const theme = json?.theme || json?.content?.theme;
        if (theme) {
          setThemeColors({
            primary: theme.primaryColor || theme.primary || null,
            secondary: theme.secondaryColor || theme.secondary || null,
            accent: theme.accentColor || theme.accent || null,
            font: theme.fontFamily || theme.headingFont || null,
          });
        }
      } catch { /* Main page not configured yet — ok */ }
    } catch (error) {
      if (error.response?.status === 401) { navigate('/login'); return; }
      const defaults = {};
      PAGE_DEFS.forEach(p => { defaults[p.id] = { status: 'not_started', last_updated: null }; });
      setPageStatuses(defaults);
    } finally {
      setLoading(false);
    }
  }, [templateId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const isReady = useCallback((status) =>
    ['complete', 'auto_generated', 'auto_filled'].includes(status), []);

  // Only count the 11 pages we display (not cart/checkout/auth from API)
  const completedCount = useMemo(() =>
    PAGE_DEFS.filter(p => {
      const st = pageStatuses[p.id];
      return st && isReady(st.status);
    }).length,
    [pageStatuses, isReady]);

  const progress = useMemo(() => {
    if (!pageStatuses || Object.keys(pageStatuses).length === 0) return 0;
    return Math.round((completedCount / PAGE_DEFS.length) * 100);
  }, [completedCount, pageStatuses]);

  const nextStep = useMemo(() => {
    for (const id of SETUP_ORDER) {
      const st = pageStatuses[id];
      if (!st || !isReady(st.status)) return id;
    }
    return null; // All done
  }, [pageStatuses, isReady]);

  const openEditor = useCallback((pageId, panel) => {
    let url = `/visual-editor?template=${templateId}&page=${pageId}`;
    if (panel) url += `&panel=${panel}`;
    navigate(url);
  }, [templateId, navigate]);

  const storeUrl = useMemo(() => {
    if (!user?.username) return null;
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isDev
      ? `http://${user.username}.localhost:8002`
      : `https://${user.username}.vasstr.com`;
  }, [user?.username]);

  const handlePreview = useCallback(() => {
    if (storeUrl) {
      window.open(storeUrl, '_blank', 'noopener,noreferrer');
    } else {
      window.open(`/template${templateId}`, '_blank', 'noopener,noreferrer');
    }
  }, [storeUrl, templateId]);

  // ── Status badge ──────────────────────────────────────────────────────────
  const StatusBadge = ({ status }) => {
    if (isReady(status)) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
          <CheckCircle className="w-3 h-3" /> Ready
        </span>
      );
    }
    if (status === 'draft') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
          <Clock className="w-3 h-3" /> In progress
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500 border border-gray-200">
        <AlertCircle className="w-3 h-3" /> Not started
      </span>
    );
  };

  // ── Page Card ─────────────────────────────────────────────────────────────
  const PageCard = ({ page, size = 'normal' }) => {
    const st = pageStatuses[page.id] || { status: 'not_started' };
    const done = isReady(st.status);
    const Icon = page.icon;
    const isNext = nextStep === page.id;

    return (
      <div
        className={`group relative bg-white rounded-2xl border transition-all duration-200 overflow-hidden cursor-pointer
          ${isNext ? 'border-primary/40 ring-2 ring-primary/10 shadow-sm' : done ? 'border-emerald-200' : 'border-gray-200'}
          hover:border-primary hover:shadow-lg`}
        onClick={() => openEditor(page.id)}
      >
        {isNext && (
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary to-primary/50" />
        )}

        {/* Preview area */}
        <div className="p-3 pb-0">
          <MiniPreview type={page.preview} />
        </div>

        {/* Content */}
        <div className={`p-4 ${size === 'compact' ? 'p-3' : 'p-4'}`}>
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center
                ${done ? 'bg-emerald-50' : isNext ? 'bg-primary/10' : 'bg-gray-50'}`}>
                <Icon className={`w-4 h-4 ${done ? 'text-emerald-600' : isNext ? 'text-primary' : 'text-gray-500'}`} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">{page.title}</h3>
              </div>
            </div>
            <StatusBadge status={st.status} />
          </div>

          <p className="text-xs text-gray-500 mb-3 line-clamp-1">{page.description}</p>

          <div className="flex items-center justify-between">
            {st.last_updated && (
              <span className="text-[10px] text-gray-400">
                Updated {new Date(st.last_updated).toLocaleDateString()}
              </span>
            )}
            {!st.last_updated && <span />}

            <span className={`inline-flex items-center gap-1 text-xs font-medium transition-colors
              ${isNext ? 'text-primary' : 'text-gray-500 group-hover:text-primary'}`}>
              <Pencil className="w-3 h-3" />
              {done ? 'Edit' : 'Set up'}
              <ArrowRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
            </span>
          </div>
        </div>
      </div>
    );
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-gray-500 font-medium">Loading your website builder...</p>
          </div>
        </div>
      } />
    );
  }

  // ── Grouped pages ─────────────────────────────────────────────────────────
  const corePages = PAGE_DEFS.filter(p => p.category === 'core');
  const layoutPages = PAGE_DEFS.filter(p => p.category === 'layout');
  const contentPages = PAGE_DEFS.filter(p => p.category === 'content');
  const legalPages = PAGE_DEFS.filter(p => p.category === 'legal');
  const legalDone = legalPages.filter(p => isReady(pageStatuses[p.id]?.status)).length;

  const DashboardContent = (
    <div className="bg-gray-50 min-h-screen pb-20">

      {/* ── Top Header ────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">
                {templateInfo?.temp_name || `Template ${templateId}`}
              </h1>
              {storeUrl ? (
                <button
                  onClick={handlePreview}
                  className="text-xs text-primary/70 hover:text-primary hover:underline transition-colors flex items-center gap-1"
                >
                  {storeUrl.replace(/^https?:\/\//, '')}
                  <ExternalLink className="w-2.5 h-2.5" />
                </button>
              ) : (
                <p className="text-xs text-gray-500">Website Builder</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/TemplateThemes?change=true')}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Change
            </button>
            <button
              onClick={handlePreview}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
              Preview
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6">

        {/* ── Progress + Next Step ───────────────────────────────────────── */}
        <div className="mt-6 bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  Website Progress
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {completedCount} of {PAGE_DEFS.length} pages configured
                </p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-primary">{progress}%</span>
                {progress >= 30 && progress < 100 && (
                  <p className="text-[10px] text-emerald-600 font-medium">Looking good!</p>
                )}
                {progress === 100 && (
                  <p className="text-[10px] text-emerald-600 font-medium">All pages ready!</p>
                )}
              </div>
            </div>

            {/* Step dots */}
            <div className="flex gap-1">
              {PAGE_DEFS.map((page) => {
                const st = pageStatuses[page.id];
                const done = st && isReady(st.status);
                const draft = st?.status === 'draft';
                return (
                  <div
                    key={page.id}
                    className={`h-2 flex-1 rounded-full transition-colors ${
                      done ? 'bg-emerald-400' : draft ? 'bg-amber-300' : 'bg-gray-200'
                    }`}
                    title={`${page.title}: ${done ? 'Ready' : draft ? 'In progress' : 'Not started'}`}
                  />
                );
              })}
            </div>
          </div>

          {/* Smart Next Step Banner */}
          {nextStep && SETUP_MESSAGES[nextStep] && (
            <div
              className="border-t border-gray-100 px-5 py-4 bg-gradient-to-r from-primary/5 to-transparent flex items-center justify-between cursor-pointer hover:from-primary/10 transition-colors"
              onClick={() => openEditor(nextStep)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <ArrowRight className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    Next: {SETUP_MESSAGES[nextStep].title}
                  </p>
                  <p className="text-xs text-gray-500">{SETUP_MESSAGES[nextStep].desc}</p>
                </div>
              </div>
              <button className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary-dark transition-colors shadow-sm flex-shrink-0">
                <Pencil className="w-3 h-3" />
                Start editing
              </button>
            </div>
          )}
        </div>

        {/* ── Brand & Theme Quick Access ─────────────────────────────────── */}
        <div className="mt-6 bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl flex items-center justify-center">
                  <Palette className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Brand & Theme</p>
                  <p className="text-xs text-gray-500">Colors, fonts, and brand identity</p>
                </div>
              </div>

              {/* Theme color dots */}
              {themeColors && (
                <div className="flex items-center gap-2 ml-4 pl-4 border-l border-gray-200">
                  {themeColors.primary && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full border border-gray-200 shadow-sm" style={{ backgroundColor: themeColors.primary }} />
                      <span className="text-[10px] text-gray-400 uppercase">{themeColors.primary}</span>
                    </div>
                  )}
                  {themeColors.secondary && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full border border-gray-200 shadow-sm" style={{ backgroundColor: themeColors.secondary }} />
                    </div>
                  )}
                  {themeColors.accent && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full border border-gray-200 shadow-sm" style={{ backgroundColor: themeColors.accent }} />
                    </div>
                  )}
                  {themeColors.font && (
                    <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-gray-200">
                      <Type className="w-3 h-3 text-gray-400" />
                      <span className="text-[10px] text-gray-500 font-medium">{themeColors.font}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={() => openEditor('main', 'theme')}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors"
            >
              <Palette className="w-3.5 h-3.5" />
              Customize theme
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* ── Core Pages ─────────────────────────────────────────────────── */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-base font-bold text-gray-900">Core Pages</h2>
            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary">
              Essential
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {corePages.map(page => <PageCard key={page.id} page={page} />)}
          </div>
        </div>

        {/* ── Layout ─────────────────────────────────────────────────────── */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-base font-bold text-gray-900">Site Layout</h2>
            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/10 text-primary">
              Essential
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {layoutPages.map(page => <PageCard key={page.id} page={page} />)}
          </div>
        </div>

        {/* ── Content Pages ──────────────────────────────────────────────── */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-base font-bold text-gray-900">Content Pages</h2>
            <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
              Recommended
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {contentPages.map(page => <PageCard key={page.id} page={page} />)}
          </div>
        </div>

        {/* ── Legal Pages (Collapsible) ──────────────────────────────────── */}
        <div className="mt-8">
          <button
            onClick={() => setShowLegal(!showLegal)}
            className="w-full flex items-center justify-between py-3 group"
          >
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-gray-900">Legal Pages</h2>
              <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                Optional
              </span>
              {legalDone > 0 && (
                <span className="text-xs text-emerald-600 font-medium">{legalDone}/{legalPages.length} ready</span>
              )}
            </div>
            {showLegal
              ? <ChevronUp className="w-4 h-4 text-gray-400" />
              : <ChevronDown className="w-4 h-4 text-gray-400" />
            }
          </button>

          {showLegal && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
              {legalPages.map(page => <PageCard key={page.id} page={page} size="compact" />)}
            </div>
          )}

          {!showLegal && (
            <p className="text-xs text-gray-400 -mt-1">
              Pre-filled with defaults. Click to expand and customize.
            </p>
          )}
        </div>

        {/* ── Bottom Preview Section ──────────────────────────────────────── */}
        <div className="mt-12 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-base font-bold">
                {progress >= 100 ? 'Your website is ready!' : `${completedCount} of ${PAGE_DEFS.length} pages configured`}
              </h3>
              {storeUrl && (
                <p className="text-sm text-gray-400 mt-1">
                  Your store: <span className="text-white/80">{storeUrl.replace(/^https?:\/\//, '')}</span>
                </p>
              )}
            </div>
            <button
              onClick={handlePreview}
              className="flex items-center gap-1.5 px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary-dark transition-colors shadow-lg"
            >
              <ExternalLink className="w-4 h-4" />
              Preview your site
            </button>
          </div>
        </div>

      </div>
    </div>
  );

  return <Base Content={DashboardContent} />;
};

export default PageDashboard;
